import { useState, useEffect } from "react";
import {
  featureToggleManager,
  FeatureToggleKey,
  FEATURE_TOGGLES,
} from "src/lib/featureToggles";

/**
 * Hook to get the state of a specific feature toggle
 * Uses event-driven updates instead of polling for better performance
 * Can accept either a string key or the feature toggle object directly
 */
export function useFeatureToggle(featureKey: FeatureToggleKey): boolean {
  const [isEnabled, setIsEnabled] = useState(() =>
    featureToggleManager.isFeatureEnabled(featureKey),
  );

  useEffect(() => {
    // Update initial state
    setIsEnabled(featureToggleManager.isFeatureEnabled(featureKey));

    // Subscribe to real-time updates (replaces polling)
    const unsubscribe = featureToggleManager.addListener(() => {
      setIsEnabled(featureToggleManager.isFeatureEnabled(featureKey));
    });

    return unsubscribe;
  }, [featureKey]);

  return isEnabled;
}

/**
 * Simple helper to check if a feature is enabled
 * For use inside components with hooks - reactive and updates UI
 * Can accept either string key or feature toggle object directly
 */
export function useIsOn(
  feature:
    | FeatureToggleKey
    | (typeof FEATURE_TOGGLES)[keyof typeof FEATURE_TOGGLES],
): boolean {
  // If it's a feature toggle object, extract the key
  const featureKey =
    typeof feature === "string"
      ? feature
      : (Object.entries(FEATURE_TOGGLES).find(
          ([_, obj]) => obj === feature,
        )?.[0] as FeatureToggleKey);

  return useFeatureToggle(featureKey);
}

/**
 * Hook for accessing all feature toggle states
 * Useful for debug panels or bulk feature checking
 */
export function useAllFeatureToggles(): Record<string, boolean> {
  const [allStates, setAllStates] = useState(() =>
    featureToggleManager.getAllFeatureStates(),
  );

  useEffect(() => {
    // Update initial state
    setAllStates(featureToggleManager.getAllFeatureStates());

    // Subscribe to real-time updates
    const unsubscribe = featureToggleManager.addListener(() => {
      const newStates = featureToggleManager.getAllFeatureStates();
      setAllStates(newStates);
    });

    return unsubscribe;
  }, []);

  return allStates;
}

/**
 * Hook for feature toggle manager status
 * Useful for loading states
 */
export function useFeatureToggleManager() {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Check if already initialized using proper getter
    setIsInitialized(featureToggleManager.initialized);

    // Listen for initialization updates
    const unsubscribe = featureToggleManager.addListener(() => {
      setIsInitialized(featureToggleManager.initialized);
    });

    return unsubscribe;
  }, []);

  return {
    isInitialized,
    manager: featureToggleManager,
  };
}

/**
 * Hook specifically for canvas elements that provides reactive feature flag checking
 * This ensures canvas elements update when feature flags change
 */
export function useCanvasElementEnabled(elementId: string): boolean {
  const getFeatureKeyForElement = (elementId: string): string => {
    // Map element IDs to feature keys - this should match the mapping in canvas files
    const elementToFeatureMap: Record<string, string> = {
      SUMMARY_AUDIT: "CANVA_ELEMENT_SUMMARY_AUDIT",
      EXECUTIVE_SUMMARY: "CANVA_ELEMENT_EXECUTIVE_SUMMARY",
      SALES_PERFORMANCE: "CANVA_ELEMENT_SALES_PERFORMANCE",
      SALES_CHANNEL: "CANVA_ELEMENT_SALES_CHANNEL",
      CUSTOMER_JOURNEY_MAP: "CANVA_ELEMENT_CUSTOMER_JOURNEY_MAP",
      PRODUCT_DETAILS: "CANVA_ELEMENT_PRODUCT_DETAILS",
      EXTERNAL_PRODUCT_DETAILS: "CANVA_ELEMENT_EXTERNAL_PRODUCT_DETAILS",
      SCORE_REVIEWS: "CANVA_ELEMENT_SCORE_REVIEWS",
      ASPECT_OPTIMIZATION: "CANVA_ELEMENT_ASPECT_OPTIMIZATION",
      CONTENT_OPTIMIZATION: "CANVA_ELEMENT_CONTENT_OPTIMIZATION",
      PRICE_OPTIMIZATION: "CANVA_ELEMENT_PRICE_OPTIMIZATION",
      REVIEWS_OPTIMIZATION: "CANVA_ELEMENT_REVIEWS_OPTIMIZATION",
      ADS_SALES_SLIDERS: "CANVA_ELEMENT_ADS_SALES_SLIDERS",
      SATURATION_CURVE: "CANVA_ELEMENT_SATURATION_CURVE",
      GOAL_SETTING: "CANVA_ELEMENT_GOAL_SETTING",
      ADS_SANKEY: "CANVA_ELEMENT_ADS_SANKEY",
      FUNNEL: "CANVA_ELEMENT_FUNNEL",
      PRODUCT_PLACEMENT: "CANVA_ELEMENT_PRODUCT_PLACEMENT",
      ADS_SALES: "CANVA_ELEMENT_ADS_SALES",
      CAMPAIGN_METRICS: "CANVA_ELEMENT_CAMPAIGN_METRICS",
      ORGANIC_SALES: "CANVA_ELEMENT_ORGANIC_SALES",
      ORGANIC_KEYWORD_PERFORMANCE: "CANVA_ELEMENT_ORGANIC_KEYWORD_PERFORMANCE",
      ORGANIC_MISSING_TARGET: "CANVA_ELEMENT_ORGANIC_MISSING_TARGET",
      FINANCIAL_OVERVIEW: "CANVA_ELEMENT_FINANCIAL_OVERVIEW",
      CASH_FLOW: "CANVA_ELEMENT_CASH_FLOW",
      WATERFALL: "CANVA_ELEMENT_WATERFALL",
      INVENTORY_PERFORMANCE: "CANVA_ELEMENT_INVENTORY_PERFORMANCE",
      INVENTORY_OVERVIEW: "CANVA_ELEMENT_INVENTORY_OVERVIEW",
      MARKET_LEVEL_SALES_DISTRIBUTION:
        "CANVA_ELEMENT_MARKET_LEVEL_SALES_DISTRIBUTION",
      MARKET_OVERVIEW: "CANVA_ELEMENT_MARKET_OVERVIEW",
      MARKET_SHARE: "CANVA_ELEMENT_MARKET_SHARE",
      MARKET_SALES_PERFORMANCE: "CANVA_ELEMENT_MARKET_SALES_PERFORMANCE",
      BRAND_SALES_PERFORMANCE: "CANVA_ELEMENT_BRAND_SALES_PERFORMANCE",
      LAUNCH_PRODUCT: "CANVA_ELEMENT_LAUNCH_PRODUCT",
      UNDEFINED: "CANVA_ELEMENT_UNDEFINED",
      WHAT_IF_CHART: "CANVA_ELEMENT_WHAT_IF_CHART",
      DEMOGRAPHIC_TYPE: "CANVA_ELEMENT_DEMOGRAPHIC_TYPE",
      DEMOGRAPHIC_TABLE: "CANVA_ELEMENT_DEMOGRAPHIC_TABLE",
      DEMOGRAPHIC_TYPE_SHARE: "CANVA_ELEMENT_DEMOGRAPHIC_TYPE_SHARE",
      BRAND: "CANVA_ELEMENT_BRAND",
      PNL_BLOCK: "CANVA_ELEMENT_PNL_BLOCK",
      TARGITING_PROFILES: "CANVA_ELEMENT_TARGITING_PROFILES",
      DATA_ENTRY: "CANVA_ELEMENT_DATA_ENTRY",
    };

    return elementToFeatureMap[elementId] || "CANVA_ELEMENT_UNDEFINED";
  };

  const featureKey = getFeatureKeyForElement(elementId);
  const isEnabled = useFeatureToggle(featureKey as FeatureToggleKey);

  return isEnabled;
}
